import { useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { supabase } from "@/integrations/supabase/client";
import { getInterviewAudioUrl, type listInterviewSessions } from "@/server/analyze-interview";

type SessionRow = Awaited<ReturnType<typeof listInterviewSessions>>["sessions"][number];

const FILLER_WORDS = [
  "um", "uh", "like", "you know", "basically", "actually",
  "literally", "sort of", "kind of", "right",
];

const fmtTime = (s: number) =>
  `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function highlightTranscript(text: string): string {
  const escaped = text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  let html = escaped;
  for (const w of FILLER_WORDS) {
    const rx = new RegExp(`\\b(${w.replace(/ /g, "\\s+")})\\b`, "gi");
    html = html.replace(
      rx,
      '<mark style="background: rgba(255,193,7,0.35); border-radius:3px; padding:0 2px;">$1</mark>',
    );
  }
  return html;
}

function pacingTone(wpm: number): "success" | "warning" | "error" {
  if (wpm >= 130 && wpm <= 160) return "success";
  if (wpm >= 110 && wpm <= 180) return "warning";
  return "error";
}
function pacingHint(wpm: number): string {
  if (wpm < 110) return "Pace was slow — aim for 130–150 wpm to feel confident.";
  if (wpm > 180) return "Pace was fast — slow down so the interviewer can absorb your points.";
  if (wpm >= 130 && wpm <= 160) return "Excellent pacing — confident and measured.";
  return "Pacing was acceptable but not ideal — target 130–150 wpm.";
}

async function getAuthHeaders() {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error("You must be signed in.");
  return { authorization: `Bearer ${token}` };
}

type StarBreakdown = {
  situation: number; situationNote: string;
  task: number; taskNote: string;
  action: number; actionNote: string;
  result: number; resultNote: string;
};

function StarBar({ label, value, note }: { label: string; value: number; note: string }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
        <Typography variant="caption" sx={{ width: 80, fontWeight: 600 }}>{label}</Typography>
        <LinearProgress
          variant="determinate"
          value={(value / 5) * 100}
          color={value >= 4 ? "success" : value >= 2.5 ? "warning" : "error"}
          sx={{ flex: 1, height: 8, borderRadius: 4 }}
        />
        <Typography variant="caption" sx={{ minWidth: 28, textAlign: "right" }}>
          {value.toFixed(1)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ pl: "92px", display: "block" }}>
        {note}
      </Typography>
    </Box>
  );
}

export function SessionDetailDrawer({
  session,
  open,
  onClose,
}: {
  session: SessionRow | null;
  open: boolean;
  onClose: () => void;
}) {
  const theme = useTheme();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  const transcriptHtml = useMemo(
    () => (session ? highlightTranscript(session.transcript ?? "") : ""),
    [session],
  );

  useEffect(() => {
    if (!open || !session?.audio_path) {
      setAudioUrl(null);
      setAudioError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setAudioLoading(true);
      setAudioError(null);
      try {
        const headers = await getAuthHeaders();
        const res = await getInterviewAudioUrl({
          data: { sessionId: session.id },
          headers,
        });
        if (!cancelled) setAudioUrl(res.url);
      } catch (e) {
        if (!cancelled) setAudioError(e instanceof Error ? e.message : "Could not load recording.");
      } finally {
        if (!cancelled) setAudioLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, session?.id, session?.audio_path]);

  if (!session) return null;

  const wpm = Number(session.wpm) || 0;
  const starScore = Number(session.star_score) || 0;
  const starBreakdown = (session.star_breakdown ?? {}) as Partial<StarBreakdown>;
  const fillerBreakdown = (session.filler_breakdown ?? {}) as Record<string, number>;
  const wpmPct = Math.min(100, Math.round((wpm / 200) * 100));
  const starPct = Math.round((starScore / 5) * 100);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: "100%", sm: 520 }, maxWidth: "100%" } } }}
    >
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Stack direction="row" sx={{ alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              Session detail
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {session.question}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(session.created_at).toLocaleString()} · {fmtTime(Number(session.duration_sec))}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close detail">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <Box sx={{ p: 2, overflowY: "auto" }}>
        <Stack spacing={2}>
          {/* Audio playback */}
          {session.audio_path ? (
            <Box>
              <Typography variant="overline" color="text.secondary">
                Replay your answer
              </Typography>
              {audioLoading ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    Loading recording…
                  </Typography>
                </Stack>
              ) : audioError ? (
                <Alert severity="warning" sx={{ mt: 0.5 }}>{audioError}</Alert>
              ) : audioUrl ? (
                <Box sx={{ mt: 0.5 }}>
                  <audio
                    controls
                    src={audioUrl}
                    style={{ width: "100%" }}
                    preload="metadata"
                  />
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 0.5 }}>
                  Recording is no longer available.
                </Alert>
              )}
            </Box>
          ) : (
            <Alert severity="info">No audio was saved for this session.</Alert>
          )}

          <Divider />

          {/* Top metrics */}
          <Grid container spacing={2}>
            <Grid size={4}>
              <Box
                sx={{
                  p: 1.5, borderRadius: 2,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="caption" color="text.secondary">STAR score</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {starScore.toFixed(1)}
                  <Typography component="span" variant="caption" color="text.secondary">{" "}/ 5</Typography>
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={starPct}
                  color={starScore >= 4 ? "success" : starScore >= 2.5 ? "warning" : "error"}
                  sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                />
              </Box>
            </Grid>
            <Grid size={4}>
              <Box
                sx={{
                  p: 1.5, borderRadius: 2,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="caption" color="text.secondary">Pacing</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {wpm}
                  <Typography component="span" variant="caption" color="text.secondary">{" "}wpm</Typography>
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={wpmPct}
                  color={pacingTone(wpm)}
                  sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
                />
              </Box>
            </Grid>
            <Grid size={4}>
              <Box
                sx={{
                  p: 1.5, borderRadius: 2,
                  bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                }}
              >
                <Typography variant="caption" color="text.secondary">Fillers</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {Number(session.filler_count) || 0}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {session.sentiment ?? "—"}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Typography variant="caption" color="text.secondary">
            {pacingHint(wpm)}
          </Typography>

          <Divider />

          {/* STAR breakdown */}
          <Box>
            <Typography variant="overline" color="text.secondary">STAR breakdown</Typography>
            <Box sx={{ mt: 1 }}>
              <StarBar
                label="Situation"
                value={Number(starBreakdown.situation) || 0}
                note={starBreakdown.situationNote ?? ""}
              />
              <StarBar
                label="Task"
                value={Number(starBreakdown.task) || 0}
                note={starBreakdown.taskNote ?? ""}
              />
              <StarBar
                label="Action"
                value={Number(starBreakdown.action) || 0}
                note={starBreakdown.actionNote ?? ""}
              />
              <StarBar
                label="Result"
                value={Number(starBreakdown.result) || 0}
                note={starBreakdown.resultNote ?? ""}
              />
            </Box>
          </Box>

          {/* Filler breakdown */}
          {Object.keys(fillerBreakdown).length > 0 && (
            <Box>
              <Typography variant="overline" color="text.secondary">Filler words</Typography>
              <Stack direction="row" useFlexGap sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                {Object.entries(fillerBreakdown)
                  .sort((a, b) => Number(b[1]) - Number(a[1]))
                  .map(([w, n]) => (
                    <Chip key={w} size="small" label={`${w} · ${n}`} color="warning" variant="outlined" />
                  ))}
              </Stack>
            </Box>
          )}

          <Divider />

          {/* Coach feedback */}
          {session.feedback && (
            <Box>
              <Typography variant="overline" color="text.secondary">Coach feedback</Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>{session.feedback}</Typography>
            </Box>
          )}

          {/* Transcript */}
          <Box>
            <Typography variant="overline" color="text.secondary">
              Transcript (fillers highlighted)
            </Typography>
            <Box
              sx={{
                mt: 0.5, p: 1.5, borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
                fontSize: 14, lineHeight: 1.6, whiteSpace: "pre-wrap",
              }}
              dangerouslySetInnerHTML={{ __html: transcriptHtml }}
            />
          </Box>
        </Stack>
      </Box>
    </Drawer>
  );
}
